import MemberSettingsRenderer from "@components/utils/MemberSettingsRenderer";
import { OnboardMode, Paths } from "@constants";
import { useMemberSettings } from "@hooks/useMemberSettings";
import { RootState } from "@stores/index";
import { _MemberSettingOnboardDialogLayout } from "@types";
import { getStoredCredential } from "@utils/credentials";
import { MemberSettingAuthMode, delay } from "kz-ui-sdk";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { DialogPhonePin, DialogUsernamePassword } from "./components";
import {
  EVENT_CLOSE_ONBOARDING_DIALOG,
  EVENT_ONBOARDING_DIALOG_CLOSED,
  EVENT_OPEN_ONBOARDING_DIALOG,
  OnboardingDialogStep,
} from "./constants";
import { OnboardingDialogOption, openOBDialogEventQueue } from "./utils";

const POST_LOGIN_INFO_PATHS = [
  Paths.PRIVATE.AUTH_BANK_ACCOUNT_PAGE,
  Paths.PRIVATE.AUTH_LEGAL_NAME_PAGE,
  Paths.PRIVATE.AUTH_BANK_LEGAL_NAME_PAGE,
];

export default function OnboardingDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [onboardingMode, setOnboardingMode] = useState<OnboardMode>();
  const [step, setStep] = useState<OnboardingDialogStep>();
  const refCallback = useRef<() => void>();
  const pathname = useLocation().pathname;
  const { onboardDialogLayout } = useMemberSettings();
  const [layout, setLayout] = useState<_MemberSettingOnboardDialogLayout>(onboardDialogLayout);
  const refHandlePostLoginInfoBack = useRef<() => void>();
  const refOnClose = useRef<() => void>();
  const [redirectHomeOnSuccess, setRedirectHomeOnSuccess] = useState(false);
  const isOnboardingHidden = useSelector(({ ui }: RootState) => ui.onboardingOverlayHidden);

  const handleOpen = useCallback(
    ({
      mode,
      step: defaultStep,
      callback,
      layout: defaultLayout,
      handlePostLoginInfoBack,
      onClose,
      redirectHomeOnSuccess,
    }: OnboardingDialogOption) => {
      // avoid override current post login step
      if (isOpen && isInPostLoginStep(step) && isInPostLoginStep(defaultStep)) {
        return;
      }

      const hasSavedPhoneNumber = !!getStoredCredential().clientId;
      if (hasSavedPhoneNumber) {
        setOnboardingMode(OnboardMode.LOGIN);
      } else {
        setOnboardingMode(mode ?? OnboardMode.LOGIN);
      }
      setStep(defaultStep || OnboardingDialogStep.Onboarding);
      setLayout(defaultLayout || onboardDialogLayout);
      refCallback.current = callback;
      refHandlePostLoginInfoBack.current = handlePostLoginInfoBack ?? undefined;
      refOnClose.current = onClose ?? undefined;
      setRedirectHomeOnSuccess(redirectHomeOnSuccess ?? true);
      setIsOpen(true);
    },
    [onboardDialogLayout, isOpen, step],
  );

  const handleClose = useCallback(() => {
    const currentStep = step;
    setIsOpen(false);
    // reset onboarding step
    delay(500).then(() => {
      // in case another event open the drawer, we don't want to reset the step
      if (!isOpen) {
        setStep(OnboardingDialogStep.Onboarding);
        // reset layout to default
        setLayout(onboardDialogLayout);
      }
    });
    refOnClose.current?.();
    // Dispatch internal close event so all listeners (e.g., useDailyRewardModalTrigger) can react
    window.dispatchEvent(
      new CustomEvent<{ step?: OnboardingDialogStep }>(EVENT_ONBOARDING_DIALOG_CLOSED, {
        detail: { step: currentStep },
      }),
    );
  }, [isOpen, onboardDialogLayout, step]);

  // register open dialog event
  useEffect(() => {
    const handleOpenEvent = (event: CustomEvent<OnboardingDialogOption>) => {
      handleOpen(event.detail);
    };
    window.addEventListener(EVENT_OPEN_ONBOARDING_DIALOG, handleOpenEvent as EventListener);
    // Trigger the event queue to be ready
    openOBDialogEventQueue.setReady(true);

    return () => {
      openOBDialogEventQueue.clearPendingCalls();
      window.removeEventListener(EVENT_OPEN_ONBOARDING_DIALOG, handleOpenEvent as EventListener);
    };
  }, [handleOpen]);

  // register close dialog event
  useEffect(() => {
    window.addEventListener(EVENT_CLOSE_ONBOARDING_DIALOG, handleClose);

    return () => {
      window.removeEventListener(EVENT_CLOSE_ONBOARDING_DIALOG, handleClose);
    };
  }, [handleClose]);

  // hide onboarding drawer if current route is post login info page
  useEffect(() => {
    if (POST_LOGIN_INFO_PATHS.some((path) => pathname.includes(path)) && isOpen) {
      handleClose();
    }
  }, [pathname, handleClose, isOpen]);

  const navigateStep = useCallback((step: OnboardingDialogStep, state?: Record<string, unknown>) => {
    setStep(step);
  }, []);

  return (
    <>
      <MemberSettingsRenderer
        feature={"authMode"}
        match={MemberSettingAuthMode.PhonePin}
      >
        {step && onboardingMode && (
          <DialogPhonePin
            isOpen={isOpen}
            handleClose={handleClose}
            stepNavigate={navigateStep}
            onboardingMode={onboardingMode}
            step={step}
            callback={refCallback.current}
            layout={layout}
            handlePostLoginInfoBack={refHandlePostLoginInfoBack.current}
            redirectHomeOnSuccess={redirectHomeOnSuccess}
          />
        )}
      </MemberSettingsRenderer>
      <MemberSettingsRenderer
        feature={"authMode"}
        match={MemberSettingAuthMode.UsernamePassword}
      >
        {step && onboardingMode && (
          <DialogUsernamePassword
            isOpen={isOpen}
            handleClose={handleClose}
            stepNavigate={navigateStep}
            onboardingMode={onboardingMode}
            step={step}
            callback={refCallback.current}
            layout={layout}
            handlePostLoginInfoBack={refHandlePostLoginInfoBack.current}
            redirectHomeOnSuccess={redirectHomeOnSuccess}
            isHidden={isOnboardingHidden}
          />
        )}
      </MemberSettingsRenderer>
    </>
  );
}

const isInPostLoginStep = (step?: OnboardingDialogStep) => {
  if (!step) return false;
  return [
    OnboardingDialogStep.AddBankAccount,
    OnboardingDialogStep.AddLegalName,
    OnboardingDialogStep.AddBankLegalName,

    OnboardingDialogStep.UpdateBankAccount,
    OnboardingDialogStep.UpdateLegalName,
    OnboardingDialogStep.UpdateBankLegalName,
  ].includes(step);
};
